import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { FileDropzoneComponent } from '../../../../../shared/components/file-dropzone/file-dropzone.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { VendaDocumentoService } from '../../../../../shared/services/apis/vendas/venda-documento.service';
import { VendaAnexoSalvarStruct } from '../../../../../shared/services/structs/vendas/venda-anexo-salvar.struct';

/**
 * Anexos do documento (§9.3): qualquer tipo de arquivo, 2 MB por arquivo, internos — nao sao
 * impressos nem enviados ao cliente.
 *
 * A lista VIVE AQUI e vai inteira no salvamento, como estado final: o que tem id fica, o que tem
 * base64 entra, e o que o usuario tirou da lista o servidor apaga. E o mesmo contrato do cadastro
 * de Pessoa.
 *
 * NAO esta no FormGroup de proposito. O form guarda valor que o usuario digita; anexo e arquivo,
 * com upload, tamanho e download proprios — e um FormControl com base64 dentro faria cada
 * `valueChanges` do documento carregar megabytes.
 */
@Component({
    selector: 'app-vd-anexos',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, FileDropzoneComponent],
    templateUrl: './vd-anexos.component.html',
    styleUrl: './vd-anexos.component.css',
})
export class VdAnexosComponent {
    @Input() public anexos: VendaAnexoSalvarStruct[] = [];
    @Input() public isSomenteLeitura: boolean = false;

    @Output() public anexosChange = new EventEmitter<VendaAnexoSalvarStruct[]>();

    /** §9.3: "Limite de 2 MB por arquivo". O servidor confere de novo, sobre os bytes. */
    private readonly limiteBytes = 2 * 1024 * 1024;

    public isLendo: boolean = false;

    constructor(
        private alertService: AlertService,
        private vendaDocumentoService: VendaDocumentoService,
    ) {}

    public processarArquivos(arquivos: FileList): void {
        const aceitos: File[] = [];

        for (let i = 0; i < arquivos.length; i++) {
            const arquivo = arquivos[i];

            if (arquivo.size > this.limiteBytes) {
                this.alertService.show('Aviso', `O arquivo "${arquivo.name}" excede 2 MB.`, AlertType.warning);
                continue;
            }

            aceitos.push(arquivo);
        }

        if (aceitos.length === 0) return;

        this.isLendo = true;
        let pendentes = aceitos.length;

        for (const arquivo of aceitos) {
            const leitor = new FileReader();

            leitor.onload = () => {
                const resultado = String(leitor.result ?? '');

                this.anexos = [
                    ...this.anexos,
                    {
                        nomeArquivo: arquivo.name,
                        tamanhoBytes: arquivo.size,
                        base64: resultado.split(',')[1] ?? '',
                    },
                ];

                if (--pendentes === 0) {
                    this.isLendo = false;
                    this.anexosChange.emit(this.anexos);
                }
            };

            leitor.onerror = () => {
                this.alertService.show('Erro', `Não foi possível ler "${arquivo.name}".`, AlertType.error);

                if (--pendentes === 0) {
                    this.isLendo = false;
                    this.anexosChange.emit(this.anexos);
                }
            };

            leitor.readAsDataURL(arquivo);
        }
    }

    public remover(indice: number): void {
        this.anexos = this.anexos.filter((_, i) => i !== indice);
        this.anexosChange.emit(this.anexos);
    }

    /**
     * So para anexo JA GRAVADO. O que ainda esta na lista como base64 nao existe no storage —
     * baixar nem faria sentido: o arquivo esta na maquina de quem acabou de escolhe-lo.
     */
    public baixar(anexo: VendaAnexoSalvarStruct): void {
        if (!anexo.idVendaAnexo) return;

        this.vendaDocumentoService.getAnexo(anexo.idVendaAnexo).subscribe({
            next: (res) => {
                if (res.isError || !res.urlArquivo) {
                    this.alertService.show('Erro', res.errorDescription ?? 'Não foi possível abrir o anexo.', AlertType.error);
                    return;
                }

                const link = document.createElement('a');
                link.href = res.urlArquivo;
                link.download = anexo.nomeArquivo;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            },
            error: (err) => this.alertService.show('Erro', err.message, AlertType.error),
        });
    }

    public formatarTamanho(bytes: number | null | undefined): string {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    }
}
